// src/features/booking/BookDialog.tsx
// Book a resource: pick a slot from availability, confirm, create the booking.
// Silently resolves the effective policy/numbering context on open (ResolveBookingContext),
// shows the cancellation policy (GetEffectivePolicy), double-checks the chosen slot
// (CheckBookingAvailability) right before submitting, then CreateBooking.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, AutoGrowTextarea } from '@/components/form';
import { addDaysIso, errMessage, fmtDate, fmtMoney, fmtTime } from './lib';
import { GetAvailableSlots } from '@core/api/endpoints/booking/get_available_slots';
import { GetEffectivePolicy } from '@core/api/endpoints/booking/get_effective_policy';
import { ResolveBookingContext } from '@core/api/endpoints/booking/resolve_booking_context';
import { CheckBookingAvailability } from '@core/api/endpoints/booking/check_booking_availability';
import { CreateBooking } from '@core/api/endpoints/booking/create_booking';
import type { BookableResource, BookingPolicy, BookingSlot } from '@core/types/booking';

interface Props {
  open: boolean;
  onClose: () => void;
  resource: BookableResource;
  scope: { productId: string; platformId: string; tenantId: string };
  onBooked: () => void;
}

export function BookDialog({ open, onClose, resource, scope, onBooked }: Props) {
  const { t, locale } = useI18n();
  const resourceId = resource.entity?.id ?? '';

  const [slots, setSlots] = useState<BookingSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [selected, setSelected] = useState<BookingSlot | null>(null);
  const [policy, setPolicy] = useState<BookingPolicy | null>(null);
  const [guestCount, setGuestCount] = useState('1');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !resourceId) return;
    setSelected(null);
    setError(null);
    setGuestCount('1');
    setNotes('');
    setLoadingSlots(true);

    GetAvailableSlots(resourceId, {
      date_from: addDaysIso(0),
      date_to: addDaysIso(14),
    })
      .then((res) => setSlots(res.slots ?? []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));

    GetEffectivePolicy(resourceId)
      .then((res) => setPolicy(res.policy ?? null))
      .catch(() => setPolicy(null));

    // Silent context resolution — powers numbering/policy resolution for
    // callers that assemble a booking from a broader scope; not surfaced
    // directly, matching the entity-schedule "context resolution" pattern.
    const platformEntity = { type_id: '', type_name: 'platform', id: scope.platformId, platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId };
    ResolveBookingContext({
      scope_entity: resource.entity,
      scope_chain: resource.entity ? [resource.entity, platformEntity] : [platformEntity],
      booking_type_id: resource.booking_type_id,
    }).catch(() => {});
  }, [open, resourceId, scope.productId, scope.platformId, scope.tenantId, resource.entity, resource.booking_type_id]);

  const submit = async () => {
    if (!selected?.start_time || !selected?.end_time) {
      setError(t('booking.book.pickSlot'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const check = await CheckBookingAvailability(resource.booking_type_id || 'booking', {
        booking_type_id: resource.booking_type_id,
        resource: resource.entity,
        start_time: selected.start_time,
        end_time: selected.end_time,
        guest_count: parseInt(guestCount || '1', 10),
      }).catch(() => null);

      if (check && !check.available) {
        setError(check.unavailable_reason || t('booking.book.slotUnavailable'));
        setSlots(check.alternative_slots?.length ? check.alternative_slots : slots);
        setSubmitting(false);
        return;
      }

      await CreateBooking({
        entity: { type_id: '', type_name: 'booking', id: '', platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId },
        bookable_resource: resource.entity,
        start_time: selected.start_time,
        end_time: selected.end_time,
        guest_count: parseInt(guestCount || '1', 10),
        customer_notes: notes || undefined,
        form_answers: [],
      });
      setSubmitting(false);
      onBooked();
      onClose();
    } catch (e) {
      setSubmitting(false);
      setError(errMessage(e, t('booking.book.error')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('booking.book.confirm')}
      canSubmit={!!selected}
      isSubmitting={submitting}
      dirty={!!notes.trim()}
      aria-label={t('booking.book.title', { name: resource.name })}
      context={<span className="truncate text-sm font-medium text-foreground">{resource.name}</span>}
    >
      <div className="space-y-4 px-3.5 pb-2 pt-3">
        {error && <Alert variant="error">{error}</Alert>}

        <div>
          <span className="mb-1.5 block text-xs text-muted-foreground">{t('booking.book.chooseSlot')}</span>
          {loadingSlots && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!loadingSlots && slots.length === 0 && <p className="text-sm text-muted-foreground">{t('booking.book.noSlots')}</p>}
          {!loadingSlots && slots.length > 0 && (
            <div className="flex max-h-56 flex-wrap gap-1.5 overflow-y-auto">
              {slots.map((s, i) => {
                const isSel = selected?.start_time === s.start_time;
                const full = s.available_capacity <= 0;
                return (
                  <button
                    key={`${s.start_time}-${i}`}
                    type="button"
                    disabled={full}
                    onClick={() => setSelected(s)}
                    className={`rounded-md border px-2.5 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-40 ${
                      isSel ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-foreground hover:bg-muted'
                    }`}
                  >
                    {fmtDate(s.start_time, locale)} {fmtTime(s.start_time, locale)}
                    {s.price_cents ? ` · ${fmtMoney(s.price_cents, s.currency, locale)}` : ''}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {resource.capacity > 1 && (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
            <span className="text-xs text-muted-foreground">{t('booking.book.guestCount')}</span>
            <input
              type="text"
              inputMode="numeric"
              value={guestCount}
              onChange={(e) => setGuestCount(e.target.value.replace(/[^0-9]/g, ''))}
              aria-label={t('booking.book.guestCount')}
              className="w-10 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
            />
          </span>
        )}

        <AutoGrowTextarea value={notes} onChange={setNotes} placeholder={`${t('booking.book.notes')}…`} minRows={1} />

        {policy && (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            <p className="mb-1 font-medium text-foreground">{t('booking.book.policyTitle')}</p>
            {policy.requires_approval && <p>{t('booking.book.policyApproval')}</p>}
            {policy.deposit_required && policy.deposit_percent !== undefined && (
              <p>{t('booking.book.policyDeposit', { percent: policy.deposit_percent })}</p>
            )}
            {policy.cancellation_tiers?.length > 0 && <p>{t('booking.book.policyCancellation')}</p>}
          </div>
        )}
      </div>
    </FormDialog>
  );
}
